import { useCallback, useEffect, useRef, useState } from 'react'
import { Zap, X, AlertCircle } from 'lucide-react'
import {
  placeTripwire,
  downloadDecoyDoc,
  getStoredBearerToken,
  type ConciergeHomeTripwires,
} from '../lib/api'
import { cn, timeAgo } from '../lib/utils'
import {
  CLIENT_DECOY_TAKEN_KEY,
  CLIENT_PLACED_DECOY_KEY,
  CLIENT_PLACE_ERROR_KEY,
  CLIENT_PLACE_HANDOFF_KEY,
  CLIENT_PLACE_INTENT_KEY,
} from '../lib/sessionKeys'

// DW-CLIENT-PLACE-WAITS-FOR-BEARER: poll cadence/cap for the mount-effect
// below. 20 x 250ms = ~5s — long enough for authStore.restoreSession's
// synchronous dw_token sync (it writes dw_token before it ever awaits
// getMe()) to land, short enough to not poll forever if the client never
// actually ends up signed in.
const BEARER_POLL_INTERVAL_MS = 250
const BEARER_POLL_MAX_ATTEMPTS = 20

// Tripwire count only — never token ids, URLs, or memos, which would leak
// the underlying detection mechanics to the client surface. Placement is the
// one exception: the ONE-TIME decoy the client must save to go place it
// themselves (DESIGN_DECISION_intel_is_work_20260826.md — "the client must
// be able to PLACE a decoy"). That response never carries a management URL,
// raw token secret, or provider admin link — see placeTripwire's contract.

// DW-CLIENT-HOME-SET-ANOTHER-PLACES-WORD-DECOY-NOT-LINK: there is no kind
// chooser on this home sheet any more. Every tripwire the household places
// from home — the first one and every "Set another" after it — is the same
// Word document decoy they download and save themselves. A canary lives in
// the same picture as the logs, and the artefact that picture hands over is
// a .docx, not a URL to copy. The `link` kind still exists in the API (the
// sign-in place uses it); home simply never offers it.

// DW-CLIENT-WORD-DECOY-DOWNLOAD: the client downloads a real .docx and saves
// it into their OWN Drive / OneDrive / SharePoint — we never write to their
// storage. The name is the whole trap, so we suggest names a thief opens and
// a colleague never would, and let them type their own instead.
const SUGGESTED_DECOY_NAMES = [
  'Bank Accounts & Wire Instructions.docx',
  'Crypto Passwords & Recovery Keys.docx',
  'Executive Remuneration & Bonus Matrix.docx',
]

// The sentence that keeps the promise on screen, not just in the brief.
const SAVE_IT_YOURSELF =
  'Save it to your own Google Drive, OneDrive or SharePoint. We never touch your storage.'

type PlacedDecoy = {
  decoy: string
  where_to_put_it: string
  download_url?: string | null
  suggested_filename?: string | null
}

type SheetState = 'closed' | 'placing' | 'error'

// DW-CLIENT-TRIPWIRES-ARE-A-HOME-SECTION: this is one of home's own
// sections — coverage count, last check, set another — not a door that only
// appears when a place handoff is in flight. Home mounts it whenever home
// paints, so `tripwires` is null whenever the team brief is missing (failed
// fetch, or no principal to fetch it for). Null is UNKNOWN coverage, never
// zero: "No tripwires in place yet." is a claim about this client's estate
// and we only make it when the brief actually said so.
export function HomeTripwiresCard({
  tripwires,
}: {
  tripwires: ConciergeHomeTripwires | null
}) {
  // Local count starts from the prop and bumps by one on a successful
  // placement — cheaper than forcing the parent to refetch the whole home
  // brief for a single number. null = the brief never arrived; a placement
  // made here leaves it null (one confirmed place does not tell us the
  // estate's total, and the dialog already says the tripwire was placed).
  const [count, setCount] = useState<number | null>(tripwires?.active_count ?? null)
  const [sheet, setSheet] = useState<SheetState>('closed')
  const [placed, setPlaced] = useState<PlacedDecoy | null>(null)
  // DW-CLIENT-WORD-DECOY-DOWNLOAD: the name the client will save the .docx
  // under. Prefilled with a suggestion, freely editable — the name IS the
  // trap, so it has to be theirs. Sanitised again server-side regardless.
  const [filename, setFilename] = useState(SUGGESTED_DECOY_NAMES[0])
  const [downloading, setDownloading] = useState(false)
  // True once the bytes have actually reached the client's machine. This is
  // the document kinds' equivalent of `copied`: the one-time decoy has left
  // this dialog, so Done/X may close.
  const [downloaded, setDownloaded] = useState(false)
  const [downloadFailed, setDownloadFailed] = useState(false)
  // DW-CLIENT-PLACE-COPY-DECOY: the decoy is select-all text only — a phone
  // sit cannot highlight-and-copy that. Tracks the Copy button's own label,
  // reset whenever a new decoy lands (component remount does this for free;
  // this covers a fresh decoy landing in the same mounted instance).
  const [copied, setCopied] = useState(false)
  // DW-CLIENT-PLACE-COPIED-SURVIVES-REMOUNT: which decoy string `copied`
  // currently describes. The handoff mount effect below sets this alongside
  // a restored `copied: true` so the reset effect (next) knows this decoy's
  // copied state was already decided and must not wipe it out from under a
  // StrictMode/getMe/ProtectedRoute remount.
  const copiedDecoyRef = useRef<string | null>(null)
  // DW-CLIENT-PLACE-COPY-PAINTED-DECOY: the sync execCommand fallback copies
  // THIS painted node's contents — never a synthetic hidden textarea, which
  // Safari/iOS treat as not user-visible and silently no-op or copy empty.
  const decoyNodeRef = useRef<HTMLElement | null>(null)

  // DW-CLIENT-TRIPWIRES-ARE-A-HOME-SECTION: this section paints with home,
  // BEFORE the team brief has landed — so the brief's count arrives as a prop
  // change, not as the initial state. Adopt it when the prop itself changes
  // (unknown → the real number, or a later refetch); a placement made here
  // still survives, because this only fires when the incoming value differs
  // from the last one this card was handed.
  const propCount = tripwires?.active_count ?? null
  const lastPropCountRef = useRef<number | null>(propCount)
  useEffect(() => {
    if (lastPropCountRef.current === propCount) return
    lastPropCountRef.current = propCount
    setCount(propCount)
  }, [propCount])

  // DW-CLIENT-PLACE-HANDOFF-SURVIVES-CONSUME: the decoy/error payload keys
  // are removed here — the moment the client actually closes the sheet —
  // rather than in the mount effects below. A remount before that (StrictMode,
  // a parent re-render off ClientDashboard's team-brief fetch, ProtectedRoute
  // re-evaluating) must still find the payload and repaint it.
  //
  // DW-CLIENT-PLACE-DECOY-SURVIVES-BACKDROP: a decoy showing means the only
  // place this one-time secret has ever been shown is right here — closing
  // before it is copied would lose it for good. Refuse to close in that
  // state regardless of Bearer: a Bearer only proves the client is signed
  // in, not that the decoy ever left this dialog. The first authenticator
  // sit DOES have a Bearer (the login JWT) and is exactly the case this
  // guards — X/Done there must still wait for Copy, or the client's one
  // sit burns the only decoy with nothing saved.
  //
  // DW-CLIENT-PLACE-COPIED-THEN-DONE: the one exception is once the client
  // has actually copied the decoy to their clipboard — the secret has left
  // this dialog by their own action, so there is nothing left here to lose.
  // X/Done may then close, Bearer or not. The backdrop click below stays
  // gated on `placed` alone regardless of `copied` — a miss-click must never
  // dismiss a showing decoy, only the explicit X/Done buttons can.
  //
  // DW-CLIENT-WORD-DECOY-DOWNLOAD: a document decoy has no Copy button — the
  // file itself is the thing that must leave this dialog. So for those kinds
  // the same guard reads `downloaded` instead of `copied`: closing before the
  // .docx has been fetched would lose the only copy of it.
  const close = useCallback(() => {
    if (placed) {
      const needsDownload = Boolean(placed.download_url)
      if (needsDownload ? !downloaded : !copied) return
    }
    setSheet('closed')
    setPlaced(null)
    setDownloadFailed(false)
    sessionStorage.removeItem(CLIENT_PLACED_DECOY_KEY)
    sessionStorage.removeItem(CLIENT_PLACE_ERROR_KEY)
  }, [placed, copied, downloaded])

  // DW-CLIENT-PLACE-ON-SIGNIN: LoginPage may already have placed a Link
  // tripwire itself, in the same tick the session was issued, before this
  // card ever mounted — it hands the one-time decoy off here rather than
  // relying on this card to notice the intent and re-place. Copy it into
  // local state and show it, but leave the key in sessionStorage until the
  // client closes the sheet (close() above) — a remount must still find it.
  // Never call placeTripwire again for this load either way.
  useEffect(() => {
    const raw = sessionStorage.getItem(CLIENT_PLACED_DECOY_KEY)
    if (!raw) return
    try {
      const parsed = JSON.parse(raw) as {
        decoy?: unknown
        where_to_put_it?: unknown
        copied?: unknown
        download_url?: unknown
        suggested_filename?: unknown
        downloaded?: unknown
      }
      if (typeof parsed.decoy === 'string' && typeof parsed.where_to_put_it === 'string') {
        setPlaced({
          decoy: parsed.decoy,
          where_to_put_it: parsed.where_to_put_it,
          // DW-CLIENT-WORD-DECOY-DOWNLOAD: a document decoy restored across a
          // remount must come back WITH its download route, or Done stays
          // trapped behind a button that is no longer painted.
          download_url:
            typeof parsed.download_url === 'string' ? parsed.download_url : null,
          suggested_filename:
            typeof parsed.suggested_filename === 'string'
              ? parsed.suggested_filename
              : null,
        })
        if (parsed.downloaded === true) {
          copiedDecoyRef.current = parsed.decoy
          setDownloaded(true)
        }
        setSheet('placing')
        setCount((c) => (c === 0 ? 1 : c))
        // DW-CLIENT-PLACE-COPIED-SURVIVES-REMOUNT: adopt a restored Copied
        // for this same decoy — mark the ref so the reset effect below
        // recognizes this decoy's copied state is already decided and
        // leaves it alone.
        if (parsed.copied === true) {
          copiedDecoyRef.current = parsed.decoy
          setCopied(true)
        }
      }
    } catch {
      // Malformed handoff — nothing to show. LoginPage already placed it
      // server-side, so this is a display miss only, not a double-place.
    }
    // Mount-only: a one-time handoff from LoginPage, consumed immediately
    // above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // DW-CLIENT-PLACE-COPIED-SURVIVES-REMOUNT: reset Copied only when the
  // decoy string actually changes to a different URL (a fresh choose() /
  // placeWordDecoy() / auto-place) — not on the render where a remount restores
  // the SAME decoy the mount effect above just adopted a Copied for.
  useEffect(() => {
    if (!placed) return
    if (copiedDecoyRef.current === placed.decoy) return
    copiedDecoyRef.current = placed.decoy
    setCopied(false)
    // DW-CLIENT-WORD-DECOY-DOWNLOAD: a fresh decoy is a fresh file — the
    // previous one's Downloaded must not vouch for it, and the name goes back
    // to a suggestion rather than whatever the last placement was called.
    setDownloaded(false)
    setDownloadFailed(false)
    setFilename(SUGGESTED_DECOY_NAMES[0])
  }, [placed?.decoy])

  // DW-CLIENT-PLACE-COPY-DECOY: copies the decoy only — never
  // where_to_put_it, never a management URL. Must work with no Bearer (the
  // decoy-survives-getMe-401 sit): this never touches auth, never calls
  // close() or placeTripwire, and never removes CLIENT_PLACED_DECOY_KEY.
  //
  // DW-CLIENT-PLACE-COPY-SURVIVES-DENIED-CLIPBOARD: Safari and in-app
  // browsers often have no Clipboard API, or deny writeText outright.
  //
  // DW-CLIENT-PLACE-COPY-SAME-CLICK: Safari/iOS treat a rejected Clipboard
  // promise as having escaped the user gesture — document.execCommand('copy')
  // run inside that rejection handler then silently returns false. So the
  // execCommand('copy') fallback runs synchronously right here in the click
  // handler, in parallel with writeText's promise, instead of waiting for
  // writeText to reject. Whichever path lands first marks Copied; the guard
  // below stops the other from doing it twice or, if writeText later
  // rejects, undoing it. Only if BOTH paths fail does the button stay "Copy"
  // with no persist and no toast claiming the place itself failed.
  //
  // DW-CLIENT-PLACE-COPY-PAINTED-DECOY: the sync fallback Range-selects the
  // already-painted decoy `<code>` node (via decoyNodeRef) and copies THAT
  // selection — never a freshly created hidden/opacity-0 textarea, which
  // Safari/iOS do not treat as user-visible and so execCommand('copy') on it
  // silently fails or copies nothing. Decoy only — never where_to_put_it,
  // which lives in its own paragraph outside this node.
  const copyDecoy = useCallback(() => {
    if (!placed) return
    const { decoy, where_to_put_it } = placed

    let alreadyCopied = false
    const markCopied = () => {
      if (alreadyCopied) return
      alreadyCopied = true
      setCopied(true)
      copiedDecoyRef.current = decoy
      // DW-CLIENT-PLACE-COPIED-SURVIVES-REMOUNT: persist Copied onto the
      // existing handoff JSON so a remount (StrictMode / getMe /
      // ProtectedRoute) restores the button as already-Copied instead of
      // wiping it and re-trapping Done/X behind close()'s no-Bearer guard.
      // Never a token — decoy + where_to_put_it + copied only.
      sessionStorage.setItem(
        CLIENT_PLACED_DECOY_KEY,
        JSON.stringify({ decoy, where_to_put_it, copied: true }),
      )
      // DW-CLIENT-PLACE-SECOND-SIT-DOES-NOT-RE-PLACE: the one-time decoy has
      // now left this tab via the clipboard — the next /client sign-in in
      // this tab must not place another one.
      sessionStorage.setItem(CLIENT_DECOY_TAKEN_KEY, '1')
    }

    const clipboard = navigator.clipboard
    if (clipboard?.writeText) {
      // Fire-and-forget: a rejection must not block or undo the synchronous
      // execCommand attempt below, and a rejection alone must never unset a
      // Copied that execCommand already landed.
      clipboard.writeText(decoy).then(markCopied, () => {})
    }

    try {
      const node = decoyNodeRef.current
      if (node) {
        const selection = window.getSelection()
        const range = document.createRange()
        range.selectNodeContents(node)
        selection?.removeAllRanges()
        selection?.addRange(range)
        const ok = document.execCommand('copy')
        if (ok) markCopied()
      }
    } catch {
      // Both copy paths failed — leave the button reading "Copy".
    }
  }, [placed])

  // DW-CLIENT-WORD-DECOY-DOWNLOAD: pull the .docx over the authenticated
  // route and hand it to the browser as a save. We do NOT write it into the
  // client's Google Drive or SharePoint — they save it themselves, which is
  // the whole point: the only thing we ever touch is this download.
  //
  // The bytes come back as a Blob (the route is Bearer-gated, so a plain
  // <a href> would fetch it anonymously and 401), then an object URL and a
  // synthetic anchor click turn it into a real file on their machine.
  const downloadDecoy = useCallback(async () => {
    if (!placed?.download_url) return
    const chosen =
      filename.trim() || placed.suggested_filename || SUGGESTED_DECOY_NAMES[0]
    setDownloading(true)
    setDownloadFailed(false)
    try {
      const blob = await downloadDecoyDoc(placed.download_url, chosen)
      const objectUrl = URL.createObjectURL(blob)
      const anchor = document.createElement('a')
      anchor.href = objectUrl
      anchor.download = chosen
      document.body.appendChild(anchor)
      anchor.click()
      anchor.remove()
      URL.revokeObjectURL(objectUrl)

      setDownloaded(true)
      // The file has left this dialog — record that so a remount restores a
      // closeable sheet instead of re-trapping Done behind the guard above,
      // and so the next sign-in in this tab does not place another decoy.
      sessionStorage.setItem(
        CLIENT_PLACED_DECOY_KEY,
        JSON.stringify({
          decoy: placed.decoy,
          where_to_put_it: placed.where_to_put_it,
          download_url: placed.download_url,
          suggested_filename: placed.suggested_filename ?? null,
          downloaded: true,
        }),
      )
      sessionStorage.setItem(CLIENT_DECOY_TAKEN_KEY, '1')
    } catch {
      // Honest failure: the tripwire IS placed and still fires — it is only
      // the file that did not arrive. Never claim the placement failed.
      setDownloadFailed(true)
    } finally {
      setDownloading(false)
    }
  }, [placed, filename])

  // DW-CLIENT-ZERO-PLACE-IS-THE-JOB: at zero tripwires the job is to place
  // one, not to browse kinds — this places a Word document tripwire directly,
  // the same shape as the sign-in auto-place, instead of opening the chooser.
  //
  // DW-CLIENT-ZERO-PLACE-IS-A-WORD-DECOY: word_doc, not link. A client with
  // no tripwires yet was asked to open a document — handing them a URL to
  // copy at the one moment they came to act is the wrong artefact. word_doc
  // comes back with a download_url, so the sheet below paints the filename
  // chooser and Download Decoy (.docx) they can save into their OWN Drive /
  // OneDrive / SharePoint.
  //
  // DW-CLIENT-HOME-SET-ANOTHER-PLACES-WORD-DECOY-NOT-LINK: "Set another" runs
  // this SAME job. The second tripwire a household places from home is the
  // second Word document they download, not a Link they copy — the kind
  // chooser is gone from this sheet entirely. One artefact, in the same
  // picture as the logs, both times.
  const placeWordDecoy = useCallback(async () => {
    // DW-CLIENT-PLACE-NO-BEARER-IS-THE-DOOR: a login that never reached a
    // principal (getMe 401 during a place-in-flight sign-in) can still leave
    // dw_token wiped in some paths — placeTripwire would throw "no Bearer
    // token available" and that throw would paint the same error sheet, an
    // error sheet that can never actually clear (Try again lands right back
    // here with no Bearer). Check up front instead: no Bearer means no POST,
    // no dead error sheet — drop the tab-sticky handoff/error markers and
    // close the sheet so ProtectedRoute's next render falls through to
    // LoginPage instead of repainting a card the client can never use.
    if (!getStoredBearerToken()) {
      sessionStorage.removeItem(CLIENT_PLACE_ERROR_KEY)
      sessionStorage.removeItem(CLIENT_PLACE_HANDOFF_KEY)
      setSheet('closed')
      return
    }
    setSheet('placing')
    try {
      const result = await placeTripwire('word_doc')
      setPlaced({
        decoy: result.decoy,
        where_to_put_it: result.where_to_put_it,
        download_url: result.download_url ?? null,
        suggested_filename: result.suggested_filename ?? null,
      })
      setCount((c) => (c === null ? null : c + 1))
    } catch {
      setSheet('error')
    }
  }, [])

  // DW-CLIENT-PLACE-SURVIVES-REFRESH / DW-CLIENT-PLACE-BEATS-INTERVIEW /
  // DW-CLIENT-PLACE-WAITS-FOR-BEARER: a client who signed in specifically to
  // set a tripwire (bounced off /client, then routed back here) must not
  // have to hunt for the button AND choose a kind again — open the sheet and
  // place a Word document tripwire for them, once
  // (DW-CLIENT-ZERO-PLACE-IS-A-WORD-DECOY: the same decoy the zero-tripwire
  // button places, so both first-time paths hand over a .docx to download
  // rather than a URL to copy). But the intent can land BEFORE a
  // Bearer exists (sign-in's dw_token write and this mount can race), and
  // placeTripwire refuses to POST without one — consuming the intent then
  // would silently drop the client's sign-in-to-place. So: do not consume
  // the intent until getStoredBearerToken() returns non-null, polling for
  // it up to a cap rather than looping forever. Once consumed, a remount
  // (e.g. StrictMode) never re-triggers the auto-place, and manual "Set more
  // tripwires?" clicks after this only ever open the chooser.
  useEffect(() => {
    // DW-CLIENT-PLACE-NO-CHOOSER: LoginPage's own placeOnSignIn already tried
    // and failed with a Bearer in hand — that is a real POST failure, not the
    // "no Bearer yet" race below, so show it now instead of silently retrying
    // into a home that looks like a successful "No tripwires in place yet."
    // Drop the stale intent too: a client who sees the error can retry
    // manually via "Try again", but nothing here should auto-retry behind
    // that error and mask it. CLIENT_PLACE_ERROR_KEY itself is left alone
    // here — it is removed only once the client actually leaves the error
    // sheet (close() or "Try again"), so a remount before that still finds
    // it and repaints the same honest error instead of losing it.
    if (sessionStorage.getItem(CLIENT_PLACE_ERROR_KEY)) {
      sessionStorage.removeItem(CLIENT_PLACE_INTENT_KEY)
      setSheet('error')
      return undefined
    }

    if (!sessionStorage.getItem(CLIENT_PLACE_INTENT_KEY)) return undefined

    // DW-CLIENT-PLACE-RELOAD-DOES-NOT-REPLACE: a marker is not a session — an
    // intent can still be sitting in storage (e.g. left over from before this
    // gate existed, or a race with another tab) even though this tab already
    // holds a taken decoy or one still showing un-Copied. Either way the
    // one-time secret has already been spoken for; auto-placing here would
    // burn a second one nobody asked for. The decoy-handoff effect above
    // already painted CLIENT_PLACED_DECOY_KEY if present, so just drop the
    // stale intent and never POST.
    if (sessionStorage.getItem(CLIENT_DECOY_TAKEN_KEY)) {
      sessionStorage.removeItem(CLIENT_PLACE_INTENT_KEY)
      return undefined
    }
    if (sessionStorage.getItem(CLIENT_PLACED_DECOY_KEY)) {
      sessionStorage.removeItem(CLIENT_PLACE_INTENT_KEY)
      return undefined
    }

    let cancelled = false
    let attempts = 0
    let intervalId: number | undefined

    const attemptAutoPlace = async () => {
      if (cancelled) return
      if (!sessionStorage.getItem(CLIENT_PLACE_INTENT_KEY)) {
        if (intervalId !== undefined) clearInterval(intervalId)
        return
      }
      if (!getStoredBearerToken()) {
        attempts += 1
        if (attempts >= BEARER_POLL_MAX_ATTEMPTS && intervalId !== undefined) {
          clearInterval(intervalId)
        }
        return
      }

      if (intervalId !== undefined) clearInterval(intervalId)
      sessionStorage.removeItem(CLIENT_PLACE_INTENT_KEY)
      // DW-CLIENT-PLACE-NO-CHOOSER: a client who opened /client to set a
      // tripwire is not here to pick a kind — 'placing' renders no chooser at
      // all (see the render below) — and neither does the manual "Set
      // another" path, which now places the same word_doc directly.
      setSheet('placing')
      try {
        const result = await placeTripwire('word_doc')
        if (cancelled) return
        setPlaced({
          decoy: result.decoy,
          where_to_put_it: result.where_to_put_it,
          download_url: result.download_url ?? null,
          suggested_filename: result.suggested_filename ?? null,
        })
        setCount((c) => (c === null ? null : c + 1))
      } catch {
        if (cancelled) return
        if (!getStoredBearerToken()) {
          // The Bearer vanished between the check above and the POST (e.g. a
          // race with logout) — put the intent back so a later restore
          // retries, instead of silently dropping the sign-in-to-place.
          sessionStorage.setItem(CLIENT_PLACE_INTENT_KEY, '1')
          setSheet('closed')
          return
        }
        setSheet('error')
      }
    }

    void attemptAutoPlace()
    intervalId = window.setInterval(() => void attemptAutoPlace(), BEARER_POLL_INTERVAL_MS)

    return () => {
      cancelled = true
      if (intervalId !== undefined) clearInterval(intervalId)
    }
    // Mount-only: the intent is a one-shot signal consumed (once a Bearer
    // exists) above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // DW-CLIENT-ZERO-PLACE-IS-THE-JOB: zero tripwires names the dialog after
  // the job — "Set a tripwire" — never "Set another tripwire", which only
  // applies once the brief has told us at least one exists. Unknown coverage
  // (no brief) reads the same as zero here: we cannot offer "another" without
  // knowing there is a first. Once the brief says there IS a first, the
  // in-flight sheet is honestly "Set another tripwire" — the same job the
  // button that opened it named.
  const dialogTitle = placed
    ? 'Tripwire placed'
    : !count
      ? 'Set a tripwire'
      : 'Set another tripwire'

  // The coverage line — this section's one number. Unknown (no brief) is said
  // out loud rather than rounded down to "none in place".
  const coverageLine =
    count === null
      ? 'We cannot show your tripwire coverage right now.'
      : count === 0
        ? 'No tripwires in place yet.'
        : `${count} tripwire${count === 1 ? '' : 's'} in place.`

  // DW-CLIENT-HOME-ALREADY-PLACED-NAMED-AS-WORD-ON-HOME: a bare number is not
  // a canary the household can picture. Once the brief says tripwires ARE in
  // place, this section names them as the thing they actually hold — the Word
  // document they downloaded from this very section and saved into their own
  // storage (DigitalWatch.md: "Canaries are in the same picture"). It names
  // the artefact only; never a Link, an invoice, a password file, or a second
  // Download Decoy button, because they already have the file. Home only ever
  // places `word_doc` (first place and every "Set another"); the `link` kind
  // stays in the API for the sign-in place, which is not this section.
  const alreadyPlacedLine =
    count !== null && count > 0
      ? count === 1
        ? 'That is the Word document you already downloaded and saved into your own storage.'
        : 'Those are the Word documents you already downloaded and saved into your own storage.'
      : null

  // The last check — home's second tripwire fact, and the reason the number
  // above is worth anything. No stored check is said plainly; coverage we
  // never loaded gets no check line at all rather than an invented "never".
  const lastCheckLine =
    tripwires === null
      ? null
      : tripwires.last_checked_at === null
        ? 'No check recorded yet.'
        : `Last checked ${timeAgo(tripwires.last_checked_at)}.`

  return (
    <section aria-label="Tripwires" className="mb-5">
      <h2 className="text-lg font-semibold text-navy flex items-center gap-2 mb-3">
        <Zap className="w-5 h-5 text-brass" aria-hidden="true" />
        Tripwires
      </h2>
      <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
        <p className={cn('text-sm', count ? 'text-navy' : 'text-gray-600')}>
          {coverageLine}
        </p>
        {alreadyPlacedLine && (
          <p className="text-xs text-gray-600 mt-1">{alreadyPlacedLine}</p>
        )}
        {lastCheckLine && (
          <p className="text-xs text-gray-500 mt-1">{lastCheckLine}</p>
        )}
        <button
          type="button"
          onClick={() => void placeWordDecoy()}
          className="mt-2 text-xs font-medium text-brass hover:text-brass-dark transition-colors"
        >
          {count ? 'Set another' : 'Set a tripwire'}
        </button>
      </div>

      {sheet !== 'closed' && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-navy/40 p-4"
          role="dialog"
          aria-modal="true"
          aria-label={dialogTitle}
          onClick={() => {
            // DW-CLIENT-PLACE-DECOY-SURVIVES-BACKDROP: the dimmed backdrop is
            // the classic miss-click surface — never let it dismiss a
            // one-time decoy, even with a Bearer in hand. Only the explicit
            // X/Done buttons (routed through close()) can do that.
            if (placed) return
            close()
          }}
        >
          <div
            className="w-full max-w-md rounded-2xl bg-white p-5 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between mb-3">
              <h3 className="text-base font-semibold text-navy">{dialogTitle}</h3>
              <button
                type="button"
                onClick={close}
                aria-label="Close"
                className="tap-min text-gray-400 hover:text-navy"
              >
                <X className="w-4 h-4" aria-hidden="true" />
              </button>
            </div>

            {sheet === 'error' && (
              <div className="space-y-3">
                <div
                  role="alert"
                  className="flex items-start gap-2 rounded-lg border border-danger/30 bg-danger-light p-3 text-sm text-danger"
                >
                  <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" aria-hidden="true" />
                  <p>That did not place the tripwire.</p>
                </div>
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={() => {
                      sessionStorage.removeItem(CLIENT_PLACE_ERROR_KEY)
                      // DW-CLIENT-PLACE-TRY-AGAIN-IS-THE-ZERO-PLACE: a
                      // failed place retries the same job sign-in tried —
                      // placeWordDecoy, so a Word document decoy — instead
                      // of surfacing a kind chooser. That holds whether this
                      // was the first tripwire or a "Set another"
                      // (DW-CLIENT-HOME-SET-ANOTHER-PLACES-WORD-DECOY-NOT-LINK).
                      void placeWordDecoy()
                    }}
                    className="text-sm font-medium text-brass hover:text-brass-dark"
                  >
                    Try again
                  </button>
                </div>
              </div>
            )}

            {/* DW-CLIENT-HOME-SET-ANOTHER-PLACES-WORD-DECOY-NOT-LINK: no kind
                chooser lives on this sheet. Whether the client came from a
                sign-in intent, "Set a tripwire" or "Set another", the sheet
                is placing the same Word document decoy — so the only thing
                to paint before it lands is the spinner. */}
            {sheet === 'placing' && !placed && (
              <div className="flex items-center gap-2 text-sm text-gray-600 py-2">
                <span
                  className="w-4 h-4 border-2 border-brass border-t-transparent rounded-full animate-spin"
                  aria-hidden="true"
                />
                Setting your tripwire…
              </div>
            )}

            {placed && (
              <div className="space-y-3 text-sm">
                {/* DW-CLIENT-WORD-DECOY-DOWNLOAD: a document decoy is a FILE,
                    so the client names it and downloads it — a raw beacon URL
                    is not something they can save into Drive or SharePoint,
                    and showing one as the only action was the old failure.
                    Kinds with no file keep the copyable decoy below. */}
                {placed.download_url ? (
                  <div className="space-y-2">
                    <label
                      htmlFor="decoy-filename"
                      className="block text-xs font-medium text-navy"
                    >
                      Name the file
                    </label>
                    <input
                      id="decoy-filename"
                      type="text"
                      value={filename}
                      onChange={(e) => setFilename(e.target.value)}
                      className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-navy"
                    />
                    <div className="flex flex-wrap gap-1.5">
                      {SUGGESTED_DECOY_NAMES.map((name) => (
                        <button
                          key={name}
                          type="button"
                          onClick={() => setFilename(name)}
                          className="rounded-full border border-gray-200 px-2.5 py-1 text-xs text-navy hover:bg-gray-50 transition-colors"
                        >
                          {name.replace(/\.docx$/, '')}
                        </button>
                      ))}
                    </div>
                    <button
                      type="button"
                      onClick={() => void downloadDecoy()}
                      disabled={downloading}
                      className="w-full rounded-lg bg-brass px-3 py-2.5 text-sm font-medium text-white hover:bg-brass-dark disabled:opacity-50 transition-colors"
                    >
                      {downloading ? 'Preparing…' : 'Download Decoy (.docx)'}
                    </button>
                    {downloaded && (
                      <p className="text-xs text-gray-500">
                        Downloaded. {SAVE_IT_YOURSELF}
                      </p>
                    )}
                    {downloadFailed && (
                      <div
                        role="alert"
                        className="flex items-start gap-2 rounded-lg border border-danger/30 bg-danger-light p-3 text-sm text-danger"
                      >
                        <AlertCircle
                          className="w-4 h-4 shrink-0 mt-0.5"
                          aria-hidden="true"
                        />
                        <p>
                          The tripwire is placed, but the document did not
                          download. Try again.
                        </p>
                      </div>
                    )}
                  </div>
                ) : (
                  <div>
                    <label className="block text-xs font-medium text-navy mb-1.5">
                      Your decoy
                    </label>
                    <code
                      ref={decoyNodeRef}
                      className="block bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-sm text-navy font-mono break-all select-all"
                    >
                      {placed.decoy}
                    </code>
                    <button
                      type="button"
                      onClick={copyDecoy}
                      className="mt-1.5 text-xs font-medium text-brass hover:text-brass-dark transition-colors"
                    >
                      {copied ? 'Copied' : 'Copy'}
                    </button>
                  </div>
                )}
                <p className="text-gray-600">{placed.where_to_put_it}</p>
                <div className="flex justify-end pt-1">
                  <button
                    type="button"
                    onClick={close}
                    className="text-sm font-medium text-brass hover:text-brass-dark"
                  >
                    Done
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  )
}
